<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div" @close="closeModal" class="relative z-50">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black/50" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              class="w-full max-w-md transform overflow-hidden rounded-lg bg-white text-left align-middle shadow-xl transition-all flex flex-col"
            >
              <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <h3 class="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Icon icon="mdi:alert-circle-outline" class="text-red-600 w-6 h-6" />
                  {{ title || $t('common.confirmation') }}
                </h3>
                <button
                  @click="closeModal"
                  class="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100 cursor-pointer"
                >
                  <Icon icon="mdi:close" class="w-6 h-6" />
                </button>
              </div>

              <div class="p-6">
                <p class="text-sm text-gray-600">
                  {{ message || $t('common.deleteConfirmationMessage') }}
                </p>
                <div
                  v-if="itemApiDetails"
                  class="mt-2 p-2 bg-gray-50 rounded text-sm font-medium text-gray-800"
                >
                  {{ itemApiDetails }}
                </div>
              </div>

              <div class="px-6 py-4 flex justify-end gap-3 border-t border-gray-200 bg-gray-50/50">
                <button
                  type="button"
                  class="cursor-pointer px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none transition-colors"
                  @click="closeModal"
                  :disabled="isSubmitting"
                >
                  {{ cancelLabel || $t('forms.cancel') }}
                </button>

                <button
                  type="button"
                  @click="confirmAction"
                  :disabled="isSubmitting"
                  class="inline-flex justify-center items-center rounded-md bg-red-600 hover:bg-red-700 px-6 py-2 text-sm font-medium text-white focus:outline-none cursor-pointer disabled:opacity-50 disabled:cursor-default transition-colors shadow-sm"
                >
                  <Icon
                    v-if="isSubmitting"
                    icon="eos-icons:loading"
                    class="mr-2 h-4 w-4 animate-spin"
                  />
                  {{ confirmLabel || $t('common.delete') }}
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { TransitionRoot, TransitionChild, Dialog, DialogPanel } from '@headlessui/vue'
import { Icon } from '@iconify/vue'

// Props pour rendre le composant dynamique
const props = defineProps<{
  isOpen: boolean
  title?: string
  message?: string
  itemApiDetails?: string // Ex: Le nom de l'utilisateur ou du projet à supprimer
  isSubmitting?: boolean
  confirmLabel?: string
  cancelLabel?: string
}>()

// Events pour communiquer avec le parent
const emit = defineEmits(['close', 'confirm'])

const closeModal = () => {
  if (!props.isSubmitting) {
    emit('close')
  }
}

const confirmAction = () => {
  emit('confirm')
}
</script>
